import React, { useEffect, useState } from 'react';
import { Alert, Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { LEGAL_AVAILABLE } from '../../config/Config';
import api from '../../services/api';
import LegalPdfDialog from './LegalPdfDialog';

export default function StudentSelfConsentSection({
  studentSelfConsent,
  agreeToTerms,
  onStudentSelfConsentChange,
  onAgreeToTermsChange,
  onLegalAvailabilityChange,
  consentTestId,
  termsTestId,
}) {
  const [legalDialogOpen, setLegalDialogOpen] = useState(false);
  const [legalDocumentAvailable, setLegalDocumentAvailable] = useState(null);

  useEffect(() => {
    let isMounted = true;
    api.get(LEGAL_AVAILABLE)
      .then((res) => {
        if (!isMounted) return;
        const available = res.data?.available === true;
        setLegalDocumentAvailable(available);
        onLegalAvailabilityChange?.(available);
      })
      .catch(() => {
        if (!isMounted) return;
        setLegalDocumentAvailable(false);
        onLegalAvailabilityChange?.(false);
      });
    return () => {
      isMounted = false;
    };
  }, [onLegalAvailabilityChange]);

  return (
    <>
      <Box sx={{ mt: 3, p: 2.5, borderRadius: 3, background: 'rgba(255,255,255,0.45)', border: '1px solid rgba(102, 126, 234, 0.12)' }}>
        <FormControlLabel
          control={
            <Checkbox
              name="studentSelfConsent"
              checked={studentSelfConsent}
              onChange={(e) => onStudentSelfConsentChange(e.target.checked)}
              inputProps={consentTestId ? { 'data-testid': consentTestId } : undefined}
              sx={{
                color: 'primary.main',
                '&.Mui-checked': {
                  color: 'primary.main',
                },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.5 }}>
              Are you the parent of the student or a student who is 16+?
            </Typography>
          }
          sx={{ alignItems: 'flex-start', m: 0 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, ml: 4.5, lineHeight: 1.6 }}>
          Select this only if the account is being completed by a parent on the child&apos;s behalf, or by a student aged 16 or above. We&apos;ll use it to skip the later parent-consent step when allowed.
        </Typography>
      </Box>

      {studentSelfConsent && legalDocumentAvailable === false && (
        <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
          Registration is temporarily unavailable. The agreement document is not available. Please try again later or contact support.
        </Alert>
      )}

      {studentSelfConsent && (
        <FormControlLabel
          control={
            <Checkbox
              name="agreeToTerms"
              checked={agreeToTerms}
              onChange={(e) => onAgreeToTermsChange(e.target.checked)}
              required
              disabled={legalDocumentAvailable !== true}
              inputProps={termsTestId ? { 'data-testid': termsTestId } : undefined}
              sx={{
                color: 'primary.main',
                '&.Mui-checked': {
                  color: 'primary.main',
                },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
              I agree to the{' '}
              <Typography
                component="span"
                onClick={(e) => {
                  e.preventDefault();
                  if (legalDocumentAvailable === true) setLegalDialogOpen(true);
                }}
                sx={{
                  color: '#1e3c72',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  cursor: legalDocumentAvailable === true ? 'pointer' : 'not-allowed',
                  opacity: legalDocumentAvailable === true ? 1 : 0.6,
                }}
              >
                Terms of Service
              </Typography>
              {' '}and{' '}
              <Typography
                component="span"
                onClick={(e) => {
                  e.preventDefault();
                  if (legalDocumentAvailable === true) setLegalDialogOpen(true);
                }}
                sx={{
                  color: '#1e3c72',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  cursor: legalDocumentAvailable === true ? 'pointer' : 'not-allowed',
                  opacity: legalDocumentAvailable === true ? 1 : 0.6,
                }}
              >
                Privacy Policy
              </Typography>
            </Typography>
          }
          sx={{ mt: 3, mb: 0 }}
        />
      )}

      <LegalPdfDialog
        open={legalDialogOpen}
        onClose={() => setLegalDialogOpen(false)}
        onAccept={() => onAgreeToTermsChange(true)}
        pdfUrl="/api/accounts/legal/document/"
        title="Terms of Service & Privacy Policy"
      />
    </>
  );
}
